import { CHAT_BUBBLE_MS, EMOTE_BUBBLE_MS, EMOTE_SYMBOLS } from './events';

/**
 * Speech bubbles waiting to be drawn, keyed by short id.
 *
 * Pure and time-injected, in the same spirit as SnapshotBuffer: the engine
 * hands it messages and asks what to draw, and nothing here touches a canvas.
 *
 * ONE BUBBLE PER PLAYER. A second message replaces the first rather than
 * stacking. Stacking looks reasonable with two players and becomes an
 * unreadable tower the moment someone talks quickly — and the scrollback panel
 * already keeps everything, so nothing is actually lost.
 */

/**
 * Wrapping is measured in CHARACTERS, not pixels.
 *
 * The canvas is 320x180 logical pixels drawn in a 6px font, so a rough
 * character count is within a pixel or two of a real `measureText` — and this
 * runs for every visible bubble every frame, where a text measurement per line
 * would not be free. If the font ever becomes proportional enough to matter,
 * this is the thing to revisit.
 */
const MAX_CHARS_PER_LINE = 24;
const MAX_LINES = 3;

export function wrap(text, maxChars = MAX_CHARS_PER_LINE, maxLines = MAX_LINES) {
  const words = String(text).split(' ').filter(Boolean);
  const lines = [];
  let line = '';

  for (let i = 0; i < words.length; i += 1) {
    // A single word longer than a line would otherwise loop forever or
    // overflow; hard-break it instead.
    let word = words[i];
    while (word.length > maxChars) {
      if (line) { lines.push(line); line = ''; }
      lines.push(word.slice(0, maxChars));
      word = word.slice(maxChars);
      if (lines.length >= maxLines) return lines.slice(0, maxLines);
    }

    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length <= maxChars) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
      if (lines.length >= maxLines) return lines.slice(0, maxLines);
    }
  }

  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

export default class Bubbles {
  constructor({ now = () => performance.now() } = {}) {
    this.now = now;
    /** @type {Map<number, {lines: string[], expiresAt: number, emote: boolean}>} */
    this.items = new Map();
  }

  /** @param {number} shortId @param {string} text */
  say(shortId, text) {
    const lines = wrap(text);
    if (lines.length === 0) return;

    this.items.set(shortId, {
      lines,
      expiresAt: this.now() + CHAT_BUBBLE_MS,
      emote: false,
    });
  }

  /** @param {number} shortId @param {number} index into EMOTE_SYMBOLS */
  emote(shortId, index) {
    const symbol = EMOTE_SYMBOLS[index];
    if (!symbol) return;

    this.items.set(shortId, {
      lines: [symbol],
      expiresAt: this.now() + EMOTE_BUBBLE_MS,
      emote: true,
    });
  }

  /**
   * What to draw above this player, or null.
   *
   * Expiry is checked and swept on read rather than on a timer — the engine
   * asks about every visible player every frame anyway, so a timer per bubble
   * would be pure overhead.
   */
  for(shortId) {
    const item = this.items.get(shortId);
    if (!item) return null;

    if (item.expiresAt <= this.now()) {
      this.items.delete(shortId);
      return null;
    }
    return item;
  }

  /**
   * Drop bubbles for anyone not in `visible`.
   *
   * Without this, someone who speaks then walks out of range keeps their entry
   * until it expires — invisible but retained, and a slow leak in a room where
   * players cycle through.
   */
  prune(visibleShortIds) {
    this.items.forEach((_, shortId) => {
      if (!visibleShortIds.has(shortId)) this.items.delete(shortId);
    });
  }

  clear() {
    this.items.clear();
  }
}
